/* eslint-disable max-len */
/* eslint-disable no-mixed-spaces-and-tabs */
/* eslint-disable no-console */
import React, { useContext, useState, useEffect } from 'react';
import { Box, Grid, Stack, Typography, IconButton, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DetailsIcon from '../../../../../../../../../../assets/Resources/DetailIcon.svg';
import CategoriesIcon from '../../../../../../../../../../assets/Resources/CategoriesIcon.svg';
import Playlist from '../../../../../../../../../../assets/SvgIcons/resources/playlist.svg';
import AcessIcon from '../../../../../../../../../../assets/Resources/AccessIcon.svg';
import { IoPricetagOutline } from 'react-icons/io5';
import { FaRegEdit } from 'react-icons/fa';
import { PiClockCountdownBold } from 'react-icons/pi';
import DetailsSlider from './Components/DetailsSlider';
import AccessSlider from './Components/AccessSlider';
import TagSlider from './Components/TagsSlider';
import CategorySlider from './Components/CategorySlider';
import PlaylistSlider from './Components/PlaylistSlider/PlaylistSlider';
import ActivitySlider from './Components/ActivitySlider';
import { LanguageContext } from '../../../../../../../../../../libs/Context';
import ManageContentSlider from './Components/ManageContentSlider';
import { updateVideo } from '../../../../../../../../../actions/video_management/video_management';
import { updateDocumentSilent } from '../../../../../../../../../actions/document_management/document_management';
import { updateImage } from '../../../../../../../../../actions/image_management';
import { updatePlaylist } from '../../../../../../../../../actions/playlist_management/playlists';
import { toast } from 'react-toastify';

// Define interfaces for better type safety
interface ContentData {
	title: string;
	description: string;
	thumbnail?: {
		url: string;
		canEdit?: boolean;
		canDelete?: boolean;
		source?: string;
		key?: string;
	};
}

const DetailsOptionSlider = (props) =>
{
	const { selectedTab, setSelectedTab, setOpen, typeMore,	articleCreate, imgEdit,
		    editPlaylist, SectionType, createType, CreationEditType, videoData, imageData,
		    documentData, playlistData, onVideoDataChange, onImageDataChange, onDocumentDataChange,
		    onPlaylistUpdate } = props;
	const { language } = useContext(LanguageContext);
	const [ isSubmitting, setIsSubmitting ] = useState(false);

	// Centralized content data state for better management
	const [ contentData, setContentData ] = useState<ContentData>({
		title       : '',
		description : '',
		thumbnail   : { url: '' }
	});

	// Initialize content data based on the content type
	useEffect(() =>
	{
		let initialData: ContentData = {
			title       : '',
			description : '',
			thumbnail   : { url: '' }
		};

		if (playlistData)
		{
			// ENHANCED: Map playlist structure to content data with S3 properties
			initialData = {
				title       : playlistData.name || '',
				description : playlistData.description || '',
				thumbnail   : {
					url       : playlistData.thumbnailUrl || '',
					canEdit   : true,
					canDelete : true,
					// Include S3 properties for proper thumbnail management
					source    : playlistData.thumbnailSource || 'system',
					key       : playlistData.thumbnailKey
				}
			};
		}
		else if (videoData)
		{
			initialData = {
				title       : videoData.title || videoData.videos?.[0]?.title || '',
				description : videoData.description || videoData.videos?.[0]?.description || '',
				thumbnail   : {
					url       : videoData.thumbnail?.url || videoData.videos?.[0]?.thumbnail?.url || '',
					canEdit   : videoData.thumbnail?.canEdit || videoData.videos?.[0]?.thumbnail?.canEdit,
					canDelete : videoData.thumbnail?.canDelete || videoData.videos?.[0]?.thumbnail?.canDelete
				}
			};
		}
		else if (imageData)
		{
			initialData = {
				title       : imageData.files?.[0]?.title || '',
				description : imageData.files?.[0]?.description || '',
				thumbnail   : {
					url : imageData.files?.[0]?.url || ''
				}
			};
		}
		else if (documentData)
		{
			initialData = {
				title       : documentData.files?.[0]?.title || '',
				description : documentData.files?.[0]?.description || '',
				thumbnail   : {
					url : documentData.files?.[0]?.thumbnail?.url || '/documents/pdf.png'
				}
			};
		}

		setContentData(initialData);
	}, [ playlistData, videoData, imageData, documentData ]);

	// Function to handle content data updates from child components
	const handleContentDataUpdate = (updatedData: Partial<ContentData>) =>
	{
		setContentData((prevData) =>
		{
			const newData = {
				...prevData,
				...updatedData
			};

			return newData;
		});

		// Propagate changes to parent components if they have change handlers
		if (videoData && onVideoDataChange)
		{
			onVideoDataChange(updatedData);
		}
		else if (imageData && onImageDataChange)
		{
			onImageDataChange(updatedData);
		}
		else if (documentData && onDocumentDataChange)
		{
			onDocumentDataChange(updatedData);
		}
		// Note: For playlist, we manage the state locally and save on submit
	};

	// Handle save button click
	const handleSave = async () =>
	{
		try
		{
			setIsSubmitting(true);

			// Determine which content type we're handling and call the appropriate update function
			if (videoData)
			{
				// For videos
				const updatePayload = {
					videos : [ {
						...(videoData.videos?.[0] || {}),
						title       : contentData.title,
						description : contentData.description
					} ]
				};

				await updateVideo(videoData._id, updatePayload);
			}
			else if (imageData)
			{
				// For images
				const updatePayload = {
					files : imageData.files?.map((file, index) =>
					{
						if (index === 0)
						{
							return {
								...file,
								title       : contentData.title,
								description : contentData.description
							};
						}

						return file;
					})
				};

				await updateImage(imageData._id, updatePayload);
			}
			else if (documentData)
			{
				// For documents
				const updatePayload = {
					files : documentData.files?.map((file, index) =>
					{
						if (index === 0)
						{
							return {
								...file,
								title       : contentData.title,
								description : contentData.description
							};
						}

						return file;
					})
				};

				await updateDocumentSilent(documentData._id, updatePayload);
			}
			else if (playlistData)
			{
				// ENHANCED: For playlists - map title back to name and preserve S3 properties
				const updatePayload = {
					name         : contentData.title, // Map title back to name for playlist
					description  : contentData.description,
					thumbnailUrl : contentData.thumbnail?.url || '',
					// Preserve S3 properties for proper thumbnail management
					...(contentData.thumbnail?.source && { thumbnailSource: contentData.thumbnail.source }),
					...(contentData.thumbnail?.key && { thumbnailKey: contentData.thumbnail.key })
				};

				await updatePlaylist(playlistData._id || playlistData.id, updatePayload);
				onPlaylistUpdate?.();
			}

			// Close the drawer
			setOpen(false);
		}
		catch (error)
		{
			console.error('Error updating content:', error);
			toast.error('Failed to update content');
		}
		finally
		{
			setIsSubmitting(false);
		}
	};

	const allTabs = [
		{ name: 'Details', Icon: DetailsIcon, ImageIcon: true },
		...(articleCreate !== 'Create' ? [ { name: 'Playlist', Icon: Playlist, ImageIcon: true } ] : []),
		{ name: 'Categories', Icon: CategoriesIcon, ImageIcon: true },
		{ name: 'Tags', Icon: <IoPricetagOutline fontSize={'26px'} />, ImageIcon: false },
		{ name: 'Access', Icon: AcessIcon, ImageIcon: true },
		{ name: 'Manage', Icon: <FaRegEdit fontSize={'26px'} />, ImageIcon: false },
		{ name: 'Activity', Icon: <PiClockCountdownBold fontSize={'26px'} />, ImageIcon: false }
	];

	const Tabs = (typeMore || CreationEditType === 'PodcastMultipleView')
		? allTabs.filter((tab) => [ 'Categories', 'Tags', 'Access', 'Manage' ].includes(tab.name))
		: (createType === 'Image' || createType === 'Docs' || CreationEditType === 'DocMultipleEdit' || CreationEditType === 'ImageMultipleEdit')
			? allTabs.filter((tab) => [ 'Categories', 'Tags', 'Access', 'Manage', 'Activity' ].includes(tab.name))
			: CreationEditType === 'videosCreationEdit'
				? allTabs.filter((tab) => [ 'Categories', 'Playlist', 'Tags', 'Access', 'Manage' ].includes(tab.name))
				: CreationEditType === 'playlistEdit'
					? allTabs.filter((tab) => [ 'Details' ].includes(tab.name))
					: CreationEditType === 'VideosEdit'
						? allTabs.filter((tab) => [ 'Details', 'Playlist', 'Categories', 'Tags', 'Access', 'Manage', 'Activity' ].includes(tab.name))
						: (createType === 'Videos' || CreationEditType === 'VideoMultipleEdit')
							? allTabs.filter((tab) => [ 'Playlist', 'Categories', 'Tags', 'Access', 'Manage', 'Activity' ].includes(tab.name))
							: (imgEdit === 'Edit' || createType === 'Podcast' || CreationEditType === 'DocEdit' || CreationEditType === 'ImageEdit')
								? allTabs.filter((tab) => [ 'Details', 'Categories', 'Tags', 'Access', 'Manage', 'Activity' ].includes(tab.name))
								: (editPlaylist || CreationEditType === 'PodcastEdit')
									? allTabs.filter((tab) => [ 'Details', 'Playlist' ].includes(tab.name))
									: CreationEditType === 'PodcastEditMultiple'
										? allTabs.filter((tab) => [ 'Playlist' ].includes(tab.name))
										: CreationEditType === 'playlistEdit'
											? allTabs.filter((tab) => [ 'Details', 'Tags', 'Access', 'Manage', 'Activity' ].includes(tab.name))
											: allTabs.filter((tab) => !(SectionType === 'Documents' && tab.name === 'Playlist'));

	// Determine item type for DetailsSlider
	const getItemType = () =>
	{
		if (playlistData) return 'playlist';
		if (videoData) return 'video';
		if (imageData) return 'image';
		if (documentData) return 'document';

		return 'podcast'; // default
	};

	return (
		<Grid container height='100%' width='100%' >
			{/* Left Sidebar Tabs */}
			<Grid item xs={2.5} height={'100%'} sx={{ boxShadow: '0px 4px 14px 0px rgba(0, 0, 0, 0.10)' }}>
				<Stack spacing={2} pt={3}>
					{Tabs.map((tab, index) => (
						<Stack
							key={index}
							direction='column'
							alignItems='center'
							sx={{
								cursor          : 'pointer',
								padding         : '10px 8px',
								borderRight     : selectedTab === tab.name ? '4px solid #24a4c4' : '4px solid transparent',
								backgroundColor : selectedTab === tab.name ? '#EFFBFF' : ''
							}}
							onClick={() => setSelectedTab(tab.name)}
						>
							{tab?.ImageIcon
								? (
									<Box width={'28px'} height={'28px'}>
										<img src={`${tab.Icon}`} alt='' className='invertGray' />
									</Box>
								)
								: (tab.Icon)}
							<Typography variant='body2' color={'#737373'} pt={0.2}>{tab.name}</Typography>
						</Stack>
					))}
				</Stack>
			</Grid>

			{/* Right Content Area */}
			<Grid item xs={9.5} height={'100%'} width={'100%'}>
				<Grid container height={'100%'} direction={'column'} display={'flex'}>
					<Grid item px={2} py={1}>
						<Stack direction={'row'} alignItems={'center'} display={'flex'} justifyContent={'space-between'}>
							<Typography fontSize={'22px'} fontWeight={'var(--font500)'}>{selectedTab}</Typography>
							<IconButton onClick={() => setOpen(false)}>
								<CloseIcon />
							</IconButton>
						</Stack>
					</Grid>
					<Grid item minHeight={0} pt={1} px={2} pb={1} xs sx={{ overflowY: 'auto' }}>
						{selectedTab === 'Details' &&
						<DetailsSlider
							articleCreate={articleCreate}
							data={contentData}
							onChange={handleContentDataUpdate}
							itemType={getItemType()}
						/>}
						{selectedTab === 'Playlist' && <PlaylistSlider />}
						{selectedTab === 'Categories' && <CategorySlider artcileCreate={articleCreate} />}
						{selectedTab === 'Tags' && <TagSlider />}
						{selectedTab === 'Access' && <AccessSlider />}
						{selectedTab === 'Manage' && <ManageContentSlider />}
						{selectedTab === 'Activity' && <ActivitySlider />}
					</Grid>
					{selectedTab !== 'Activity' &&
						<Grid item>
							<Stack direction={'row'} p={2} spacing={2} alignItems={'center'} justifyContent={'end'} display={'flex'}>
								<Button onClick={() => setOpen(false)} variant='outlined'>{language?.cancel}</Button>
								<Button
									variant='contained'
									sx={{ color: 'var(--whiteColor)' }}
									onClick={handleSave}
									disabled={isSubmitting}
								>
									{isSubmitting ? 'Saving...' : language?.save}
								</Button>
							</Stack>
						</Grid>}
				</Grid>
			</Grid>
		</Grid>
	);
};

export default DetailsOptionSlider;
